import { useMemo } from 'react';

/**
 * flattenSchemaFields — traverses a JSON schema recursively and returns
 * all property paths using dot-notation for nested objects and `[]` for arrays.
 *
 * @param {object} schema  — JSON schema object with `properties`
 * @param {string} prefix  — current path prefix (used in recursion)
 * @returns {string[]}     — flat list of field paths
 */
export function flattenSchemaFields(schema, prefix = '') {
  const fields = [];
  if (schema?.properties) {
    for (const [key, def] of Object.entries(schema.properties)) {
      const path = prefix ? `${prefix}.${key}` : key;
      fields.push(path);
      if (def.type === 'object' && def.properties) {
        fields.push(...flattenSchemaFields(def, path));
      }
      if (def.type === 'array' && def.items?.properties) {
        fields.push(...flattenSchemaFields(def.items, `${path}[]`));
      }
    }
  }
  return fields;
}

/**
 * useAvailableFields — derives available field names from payloadSchema or tool output_fields
 * depending on the current trigger type.
 *
 * When a resultPath is set (e.g., "medicalAppointments"), the system extracts that array
 * and enriches each item with root-level scalar fields. So the available fields for
 * variable mapping should be:
 *   - scalar fields from the root (string, number, boolean)
 *   - fields inside the array items (without the array prefix)
 *
 * - endpoint/email triggers: flatten payloadSchema properties into dot-notation paths
 * - manual/scheduled triggers: use selectedTool.output_fields names
 *
 * @param {string} triggerType      — 'manual' | 'scheduled' | 'endpoint' | 'email'
 * @param {object|null} payloadSchema — JSON schema defining the expected payload structure
 * @param {object|null} selectedTool  — tool object with optional `output_fields` array
 * @param {string} resultPath        — dot-notation path to the array of contacts (e.g., "medicalAppointments")
 * @returns {string[]}              — list of available field names
 */
export function useAvailableFields(triggerType, payloadSchema, selectedTool, resultPath) {
  return useMemo(() => {
    if ((triggerType === 'endpoint' || triggerType === 'email') && payloadSchema) {
      // When resultPath is set, show the effective fields after extraction + enrichment
      if (resultPath && payloadSchema.properties) {
        const fields = [];

        // 1. Add root-level scalar fields (they get enriched into each contact)
        for (const [key, def] of Object.entries(payloadSchema.properties)) {
          if (key === resultPath) continue; // skip the array itself
          if (def.type !== 'object' && def.type !== 'array') {
            fields.push(key);
          }
        }

        // 2. Add fields from inside the array items (these become direct contact fields)
        const arrayDef = payloadSchema.properties[resultPath];
        if (arrayDef?.type === 'array' && arrayDef.items?.properties) {
          for (const itemKey of Object.keys(arrayDef.items.properties)) {
            if (!fields.includes(itemKey)) {
              fields.push(itemKey);
            }
          }
        }

        return fields;
      }

      return flattenSchemaFields(payloadSchema);
    }
    if (selectedTool?.output_fields) {
      return selectedTool.output_fields.map(f => f.name);
    }
    return [];
  }, [triggerType, payloadSchema, selectedTool, resultPath]);
}
